import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { ClipboardList, Loader2, AlertCircle, Scale } from 'lucide-react';
import { 
  MapPin, Navigation, Camera, Upload, X, 
  Package, AlertTriangle, Building2, User,
  Calendar, Clock, FileText, CheckCircle, Lock
} from 'lucide-react';

const AddInspection = () => {
  // ------------------- Form State -------------------
  const [formData, setFormData] = useState({
    inspectorName: '',
    inspectorID: '',
    inspectionDate: new Date().toISOString().slice(0, 16),
    
    // GPS Data
    latitude: '',
    longitude: '',
    accuracy: '',
    altitude: '',
    altitudeAccuracy: '',
    heading: '',
    speed: '',
    timestamp: '',
    
    // Location Data
    region: '',
    city: '',
    subCity: '',
    woreda: '',
    kebele: '',
    
    // Market & Trader Data
    marketType: '',
    marketName: '',
    traderName: '',
    tin: '',
    businessLicense: '',
    
    // Commodity Data
    commodityChecked: '',
    commodityCategory: '',
    quantityChecked: '',
    unit: '',
    pricePerUnit: '',
    measuredPrice: '',
    
    // Violation Data
    violationType: '',
    violationDescription: '',
    violationSeverity: '',
    violationRecord: '',
    violationPenalty: '',
    violationRecordDate: '',
    actionTaken: '',
  });

  const [photos, setPhotos] = useState({
    photoScene: null,
    photoEvidence: null,
    photoLicense: null,
  });

  const [previews, setPreviews] = useState({
    photoScene: '',
    photoEvidence: '',
    photoLicense: '',
  });

  const [loading, setLoading] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationDetected, setLocationDetected] = useState(false);

  // ------------------- API Setup -------------------
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
    timeout: 30000,
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // ------------------- GPS Auto-capture on Page Load -------------------
  useEffect(() => {
    const getLocationOnLoad = () => {
      if (!navigator.geolocation) {
        Swal.fire({
          icon: 'warning',
          title: 'Geolocation Not Supported',
          text: 'Your browser does not support geolocation.',
          confirmButtonText: 'OK',
        });
        return;
      }

      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const {
            latitude,
            longitude,
            accuracy,
            altitude,
            altitudeAccuracy,
            heading,
            speed,
          } = position.coords;

          const gpsTimestamp = new Date(position.timestamp).toISOString();

          setFormData((prev) => ({
            ...prev,
            latitude: latitude.toFixed(7),
            longitude: longitude.toFixed(7),
            accuracy: accuracy ? accuracy.toFixed(2) : '',
            altitude: altitude ? altitude.toFixed(2) : '',
            altitudeAccuracy: altitudeAccuracy ? altitudeAccuracy.toFixed(2) : '',
            heading: heading ? heading.toFixed(2) : '',
            speed: speed ? speed.toFixed(2) : '',
            timestamp: gpsTimestamp,
          }));
          
          setLocationDetected(true);
          setIsLocating(false);
          
          Swal.fire({
            icon: 'success',
            title: 'Location Detected!',
            html: `<div class="text-left">
                     <p><strong>Latitude:</strong> ${latitude.toFixed(6)}</p>
                     <p><strong>Longitude:</strong> ${longitude.toFixed(6)}</p>
                   </div>`,
            timer: 3000,
            showConfirmButton: false,
            position: 'top-end',
            toast: true,
          });
        },
        (err) => {
          setIsLocating(false);
          setLocationDetected(false);
          
          let errorMessage = 'Unable to retrieve your location.';
          if (err.code === 1) errorMessage = 'Location access denied. Please enable GPS.';
          if (err.code === 2) errorMessage = 'Location unavailable. Please check your GPS.';
          if (err.code === 3) errorMessage = 'Location request timed out.';
          
          Swal.fire({
            icon: 'warning',
            title: 'Location Access',
            text: errorMessage,
            confirmButtonText: 'OK',
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    };

    setTimeout(() => {
      getLocationOnLoad();
    }, 500);
  }, []);

  // ------------------- Manual GPS Update -------------------
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      Swal.fire('Error', 'Geolocation is not supported', 'error');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const {
          latitude,
          longitude,
          accuracy,
          altitude,
          altitudeAccuracy,
          heading,
          speed,
        } = position.coords;

        const gpsTimestamp = new Date(position.timestamp).toISOString();

        setFormData((prev) => ({
          ...prev,
          latitude: latitude.toFixed(7),
          longitude: longitude.toFixed(7),
          accuracy: accuracy ? accuracy.toFixed(2) : '',
          altitude: altitude ? altitude.toFixed(2) : '',
          altitudeAccuracy: altitudeAccuracy ? altitudeAccuracy.toFixed(2) : '',
          heading: heading ? heading.toFixed(2) : '',
          speed: speed ? speed.toFixed(2) : '',
          timestamp: gpsTimestamp,
        }));
        
        setLocationDetected(true);
        setIsLocating(false);
        
        Swal.fire({
          icon: 'success',
          title: 'Location Updated!',
          html: `<div class="text-center">
                   <p><strong>Latitude:</strong> ${latitude.toFixed(6)}</p>
                   <p><strong>Longitude:</strong> ${longitude.toFixed(6)}</p>
                 </div>`,
          timer: 2000,
          showConfirmButton: false,
          position: 'top-end',
          toast: true,
        });
      },
      (err) => {
        setIsLocating(false);
        Swal.fire({
          icon: 'error',
          title: 'Location Failed',
          text: err.message || 'Unable to get location',
          confirmButtonText: 'Try Again',
        });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // ------------------- Handlers -------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      // Validate file size (max 5MB)
      if (files[0].size > 5 * 1024 * 1024) {
        Swal.fire('Error', 'File size must be less than 5MB', 'error');
        return;
      }
      
      setPhotos((prev) => ({ ...prev, [name]: files[0] }));
      const url = URL.createObjectURL(files[0]);
      setPreviews((prev) => ({ ...prev, [name]: url }));
    }
  };

  const removePhoto = (field) => {
    setPhotos((prev) => ({ ...prev, [field]: null }));
    setPreviews((prev) => {
      if (prev[field]) {
        URL.revokeObjectURL(prev[field]);
      }
      return { ...prev, [field]: '' };
    });
  };

  // ------------------- Validation -------------------
  const validate = () => {
    // Validate required fields
    const requiredFields = ['traderName', 'commodityChecked', 'quantityChecked'];
    for (let field of requiredFields) {
      if (!formData[field] || formData[field].trim() === '') {
        Swal.fire('Validation Error', `${field.replace(/([A-Z])/g, ' $1').trim()} is required`, 'warning');
        return false;
      }
    }
    
    // Validate GPS location
    if (!formData.latitude || !formData.longitude) {
      Swal.fire({
        icon: 'warning',
        title: 'Location Required',
        text: 'Please enable location access to add an inspection.',
        confirmButtonText: 'Get Location',
      }).then((result) => {
        if (result.isConfirmed) handleGetLocation();
      });
      return false;
    }
    
    // Validate quantity is a number
    if (formData.quantityChecked && isNaN(parseFloat(formData.quantityChecked))) {
      Swal.fire('Validation Error', 'Quantity must be a number', 'warning');
      return false;
    }
    
    return true;
  };

  // ------------------- Submit -------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    const submitData = new FormData();
    
    // Add all form fields
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        submitData.append(key, value);
      }
    });
    
    // Add photos
    Object.entries(photos).forEach(([key, file]) => {
      if (file) {
        submitData.append(key, file);
      }
    });

    try {
      const response = await api.post('/addinspection', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Inspection saved successfully!',
          timer: 2000,
          showConfirmButton: false,
        });
        
        // Reset form
        setFormData({
          inspectorName: '',
          inspectorID: '',
          inspectionDate: new Date().toISOString().slice(0, 16),
          latitude: '',
          longitude: '',
          accuracy: '',
          altitude: '',
          altitudeAccuracy: '',
          heading: '',
          speed: '',
          timestamp: '',
          region: '',
          city: '',
          subCity: '',
          woreda: '',
          kebele: '',
          marketType: '',
          marketName: '',
          traderName: '',
          tin: '',
          businessLicense: '',
          commodityChecked: '',
          commodityCategory: '',
          quantityChecked: '',
          unit: '',
          pricePerUnit: '',
          measuredPrice: '',
          violationType: '',
          violationDescription: '',
          violationSeverity: '',
          violationRecord: '',
          violationPenalty: '',
          violationRecordDate: '',
          actionTaken: '',
        });
        
        setPhotos({
          photoScene: null,
          photoEvidence: null,
          photoLicense: null,
        });
        
        setPreviews({
          photoScene: '',
          photoEvidence: '',
          photoLicense: '',
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Failed to save inspection',
      });
    } finally {
      setLoading(false);
    }
  };

  // Input field component
  const InputField = ({ label, name, placeholder, type = "text", required = false, icon: Icon }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 
                   rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 
                   focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
          placeholder={placeholder}
        />
      </div>
    </div>
  );

  const SelectField = ({ label, name, options, required = false, icon: Icon }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
        <select
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 
                   rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 
                   focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white
                   appearance-none cursor-pointer"
        >
          <option value="">Select {label}</option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const severityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];

  const violationTypeOptions = [
    { value: 'expired_product', label: 'Expired Product' },
    { value: 'price_violation', label: 'Price Violation' },
    { value: 'no_license', label: 'Trading Without License' },
    { value: 'short_weight', label: 'Short Weight' },
    { value: 'counterfeit', label: 'Counterfeit Product' },
    { value: 'unsafe_product', label: 'Unsafe Product' },
    { value: 'false_advertising', label: 'False Advertising' },
    { value: 'tax_evasion', label: 'Tax Evasion' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-4 md:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Add New Inspection</h1>
                <p className="text-indigo-100">Record detailed inspection information</p>
              </div>
              <div className="hidden md:block">
                <ClipboardList className="h-16 w-16 text-white opacity-20" />
              </div>
            </div>
          </div>
          
          {/* Location Status Banner */}
          <div className={`px-6 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b ${locationDetected ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center gap-3">
              {isLocating ? (
                <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
              ) : locationDetected ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              )}
              <span className={`text-sm font-medium ${locationDetected ? 'text-green-700' : 'text-yellow-700'}`}>
                {isLocating 
                  ? 'Detecting your location...' 
                  : locationDetected 
                    ? `✓ GPS Location locked (${formData.latitude}, ${formData.longitude})` 
                    : '⚠ Location not detected. Required for verification.'}
              </span>
            </div>
            <button
              type="button"
              onClick={handleGetLocation}
              disabled={isLocating}
              className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white rounded-lg 
                       hover:bg-indigo-700 transition-all disabled:opacity-50 text-sm font-medium
                       shadow-sm"
            >
              <Navigation size={16} />
              Update Location
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl overflow-hidden p-6 md:p-8">
          <div className="space-y-8">
            {/* Inspector Information */}
            <div>
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <User className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-800">Inspector Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              
              
                <InputField 
                  icon={Calendar} 
                  label="Inspection Date & Time" 
                  name="inspectionDate" 
                  type="datetime-local"
                />
              </div>
            </div>

            {/* GPS Location (Display Only) */}
            <div>
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <MapPin className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-800">GPS Location</h2>
              </div>
              <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs text-blue-700 mb-1">Latitude</label>
                    <input type="text" value={formData.latitude || 'Not detected'} readOnly disabled className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-blue-700 mb-1">Longitude</label>
                    <input type="text" value={formData.longitude || 'Not detected'} readOnly disabled className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-blue-700 mb-1">Accuracy (m)</label>
                    <input type="text" value={formData.accuracy || 'N/A'} readOnly disabled className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-blue-700 mb-1">Altitude (m)</label>
                    <input type="text" value={formData.altitude || 'N/A'} readOnly disabled className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>
                <input type="hidden" name="latitude" value={formData.latitude} />
                <input type="hidden" name="longitude" value={formData.longitude} />
                <input type="hidden" name="accuracy" value={formData.accuracy} />
                <input type="hidden" name="altitude" value={formData.altitude} />
                <input type="hidden" name="altitudeAccuracy" value={formData.altitudeAccuracy} />
                <input type="hidden" name="heading" value={formData.heading} />
                <input type="hidden" name="speed" value={formData.speed} />
                <input type="hidden" name="timestamp" value={formData.timestamp} />
              </div>
            </div>

            {/* Administrative Location */}
            <div>
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <Building2 className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-800">Administrative Location</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputField icon={MapPin} label="Region" name="region" placeholder="Enter region" />
                <InputField icon={MapPin} label="City" name="city" placeholder="Enter city" />
                <InputField icon={MapPin} label="Sub City" name="subCity" placeholder="Enter sub city" />
                <InputField icon={MapPin} label="Woreda" name="woreda" placeholder="Enter woreda" />
                <InputField icon={MapPin} label="Kebele" name="kebele" placeholder="Enter kebele" />
              </div>
            </div>

            {/* Market & Trader Information */}
            <div>
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <Building2 className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-800">Market & Trader Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <InputField icon={Building2} label="Market Type" name="marketType" placeholder="e.g., Open, Supermarket" />
                <InputField icon={Building2} label="Market Name" name="marketName" placeholder="Enter market name" />
                <InputField icon={User} label="Trader Name" name="traderName" placeholder="Enter trader name" required />
                <InputField icon={FileText} label="TIN Number" name="tin" placeholder="Enter TIN number" />
                <InputField icon={FileText} label="Business License" name="businessLicense" placeholder="Enter license number" />
              </div>
            </div>

            {/* Commodity & Pricing */}
            <div>
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <Package className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-800">Commodity & Pricing</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <InputField icon={Package} label="Commodity Checked" name="commodityChecked" placeholder="e.g., Tomato, Coffee" required />
                <InputField icon={Package} label="Commodity Category" name="commodityCategory" placeholder="e.g., Food, Electronics" />
                <InputField icon={Scale} label="Quantity Checked" name="quantityChecked" type="number" step="0.01" placeholder="Enter quantity" required />
                <InputField icon={FileText} label="Unit" name="unit" placeholder="e.g., kg, liter, piece" />
                <InputField icon={Scale} label="Price Per Unit (ETB)" name="pricePerUnit" type="number" step="0.01" placeholder="Enter price per unit" />
                <InputField icon={Scale} label="Measured Price (ETB)" name="measuredPrice" type="number" step="0.01" placeholder="Enter measured price" />
              </div>
            </div>

            {/* Violation & Action */}
            <div>
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <h2 className="text-lg font-bold text-gray-800">Violation & Action</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <SelectField icon={AlertTriangle} label="Violation Type" name="violationType" options={violationTypeOptions} />
                <SelectField icon={AlertTriangle} label="Violation Severity" name="violationSeverity" options={severityOptions} />
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Violation Description</label>
                  <textarea
                    name="violationDescription"
                    value={formData.violationDescription}
                    onChange={handleInputChange}
                    rows="3"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50 hover:bg-white"
                    placeholder="Describe the violation in detail..."
                  />
                </div>
                <InputField icon={FileText} label="Violation Record" name="violationRecord" placeholder="Record number" />
                <InputField icon={Scale} label="Violation Penalty" name="violationPenalty" placeholder="Penalty amount or action" />
                <InputField icon={Calendar} label="Violation Record Date" name="violationRecordDate" type="date" />
                <InputField icon={CheckCircle} label="Action Taken" name="actionTaken" placeholder="e.g., Warning, Fine, Seizure" />
              </div>
            </div>

            {/* Photos */}
            <div>
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <Camera className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-800">Evidence Photos</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  { key: 'photoScene', label: 'Scene Photo', name: 'photoScene' },
                  { key: 'photoEvidence', label: 'Evidence Photo', name: 'photoEvidence' },
                  { key: 'photoLicense', label: 'License Photo', name: 'photoLicense' },
                ].map(({ key, label, name }) => (
                  <div key={key} className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-500 transition-colors">
                    <label className="cursor-pointer block">
                      <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                      <span className="text-sm text-gray-600">{label}</span>
                      <input
                        type="file"
                        name={name}
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                    {previews[key] && (
                      <div className="relative mt-3 inline-block">
                        <img src={previews[key]} alt={`${label} preview`} className="max-w-full max-h-32 rounded-lg border" />
                        <button
                          type="button"
                          onClick={() => removePhoto(key)}
                          className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white 
                         py-3.5 px-6 rounded-xl font-bold text-lg hover:from-indigo-700 
                         hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed 
                         transition-all duration-200 transform hover:scale-[1.02] 
                         shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Saving Inspection...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    Save Inspection
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add missing imports


export default AddInspection;